<template>
    <div class="effect-list">
        <vue-draggable
            :list="items"
            :animation="300"
            :disabled="disabled"
            ghost-class="effect-list__ghost"
            chosen-class="effect-list__chosen"
            drag-class="effect-list__drag"
            @change="handleReorder"
        >
            <map-style-properties-list-item
                v-for="(item, index) in items"
                :id="index"
                :key="`item-${index}-${item}`"
                :item="item"
                :readonly="disabled || items?.length <= 1"
                :is-default="item === defaultStyle"
                @update="handleUpdate"
                @remove="handleRemove"
                @set-default="handleSetDefault"
            />
        </vue-draggable>

        <div v-if="items.length === 0" class="effect-list__empty">
            <p>No map styles to display</p>
        </div>
    </div>
</template>

<script setup lang="ts">
import VueDraggable from 'vuedraggable';
import MapStylePropertiesListItem from '@/components/DesignStudio/MapStyleProperties/components/list/item/MapStylePropertiesListItem.vue';

interface Emits {
    (
        event: 'update',
        data: {
            id: number;
            value: string;
        }
    ): void;
    (event: 'remove', mapStyle: string): void;
    (event: 'reorder', mapStyles: string[]): void;
    (event: 'set-default', id: number): void;
}

const props = defineProps<{
    items: string[];
    disabled?: boolean;
    defaultStyle: string;
}>();

const emit = defineEmits<Emits>();

const handleUpdate = (data: { id: number; value: string }) => {
    emit('update', data);
};

const handleRemove = (mapStyle: string) => {
    emit('remove', mapStyle);
};

const handleReorder = () => {
    emit('reorder', props.items);
};
const handleSetDefault = (id: number) => {
    emit('set-default', id);
};
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';

.effect-list {
    position: relative;

    &__empty {
        padding: 2rem;
        text-align: center;
        color: get-color('gray');
        font-style: italic;

        p {
            margin: 0;
        }
    }

    // Dragging states
    &__ghost {
        opacity: 0.5;
        background-color: get-color('primary-lightest');
        transform: rotate(2deg);
    }

    &__chosen {
        background-color: get-color('primary-lightest');
    }

    &__drag {
        transform: rotate(5deg);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
        background-color: white;
        border: 1px solid get-color('primary');
    }
}

// Accessibility improvements
@media (prefers-reduced-motion: reduce) {
    .effect-list {
        :deep(.sortable-drag) {
            transform: none !important;
        }

        &__ghost,
        &__chosen,
        &__drag {
            transform: none !important;
        }
    }
}
</style>
